import {
  Box,
  Grid,
  Icon,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import ChangeSessionStatusDialog from "@src/components/ChangeSessionStatusDialog/ChangeSessionStatusDialog";
import NothingFound from "@src/components/NothingFound";
import PageTitle from "@openup/shared/components/PageTitle/PageTitle";
import SwapPsychologistDialog from "@src/components/SwapPsychologistDialog";
import { isEmptyQueryResult } from "@src/queries/utils";
import {
  AmsterdamTimezone,
  pageSize,
  STATUS,
  STATUS_SUBJECT_FOR_CHANGE,
} from "@src/constants";
import {
  formatFirstUpperCase,
  formatStandardDateOnly,
  formatTimeOnly,
} from "@src/utils/formatter";
import Spinner from "@openup/shared/components/Spinner/Spinner";
import {
  WithSnackbarProps,
  withSnackbar,
} from "@src/components/SnackBarComponent";
import {
  HighlightOffOutlined,
  InfoOutlined,
  MoreVert as MoreVertIcon,
  PlayCircleOutline,
  SocialDistance,
  SyncAltOutlined,
} from "@mui/icons-material";
import { IconSessionLanguage } from "@src/components/Icons/IconSessionLanguage";
import {
  ResponsePsychologistConsultInformationResponsePaginated,
  ResponsePsychologistGetPsychologistConsultsResponse,
} from "@src/api/v1/data-contracts";
import Button from "@openup/shared/components/Button/Button";

type SessionListItem = ResponsePsychologistGetPsychologistConsultsResponse &
  ResponsePsychologistConsultInformationResponsePaginated;

export interface SessionsViewProps extends WithSnackbarProps {
  titleText: string;
  page: number;
  onPageChange: (page: number) => void;
  columns: { id: string; minWidth: number; width?: number }[];
  isFetching: boolean;
  consults: SessionListItem[];
  total: number;
  hasNextPage: boolean;
  showReassign?: boolean;
  showPsychologistName?: boolean;
  showJoinSession?: boolean;
  showRescheduleSession?: boolean;
  showCancelSession?: boolean;
  showCancelSessionInMenu?: boolean;
  showSessionDetails?: boolean;
  showChangeSessionStatus?: boolean;
  showCopyJoinLink: boolean;
  showReferralToolkitIcon: boolean;
  showSessionLanguage: boolean;
  showSessionStage?: boolean;
}
const SessionsView: FC<SessionsViewProps> = ({
  titleText,
  page,
  onPageChange,
  columns,
  isFetching,
  consults,
  total,
  hasNextPage,
  showReassign = false,
  showPsychologistName = false,
  showJoinSession = true,
  showRescheduleSession = true,
  showCancelSession = true,
  showCancelSessionInMenu = false,
  showSessionDetails = true,
  showChangeSessionStatus = false,
  showCopyJoinLink = false,
  showReferralToolkitIcon = false,
  showSessionLanguage = false,
  showSessionStage = false,
  snackbarShowMessage,
}) => {
  const buttonIconClassName =
    "w-8 h-8 p-0 flex justify-center items-center mr-2 p-1 rounded bg-blue-500 text-white hover:opacity-70";
  const buttonIconDisabledClassName =
    " bg-slate-300 hover:bg-slate-300 hover:opacity-100 cursor-not-allowed";

  const { t } = useTranslation();
  const navigate = useNavigate();

  const showActionsMenu =
    showRescheduleSession ||
    (showCancelSessionInMenu && showCancelSession) ||
    showCopyJoinLink;
  const [isSwapPsychologistOpen, setIsSwapPsychologistOpen] = useState(false);
  const [changeStatusOpen, setChangeStatusOpen] = useState(false);
  const [selectedConsult, setSelectedConsult] =
    useState<SessionListItem | null>(null);
  const [openTableMenus, setOpenTableMenus] = useState<{
    [id: string]: HTMLElement | null;
  }>({});
  const isInternallyScheduled = (consult: SessionListItem) =>
    !!consult.psychologistUrl;

  const handleActionsClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    id: number,
  ) => {
    event.stopPropagation();
    const tableMenus = { ...openTableMenus };
    tableMenus[id] = event.currentTarget;
    setOpenTableMenus(tableMenus);
  };
  const handleActionsClose = (id: number) => {
    const tableMenus = { ...openTableMenus };
    tableMenus[id] = null;
    setOpenTableMenus(tableMenus);
  };
  const handleClickSwapSession = (
    event: React.MouseEvent,
    consult: SessionListItem,
  ) => {
    event.stopPropagation();
    setSelectedConsult(consult);
    setIsSwapPsychologistOpen(true);
  };
  const handleClickChangeSessionStatus = (
    event: React.MouseEvent,
    consult: SessionListItem,
  ) => {
    event.stopPropagation();
    if (STATUS_SUBJECT_FOR_CHANGE.includes(consult.status)) {
      setSelectedConsult(consult);
      setChangeStatusOpen(true);
    }
  };

  const handleClickStartSession = (
    event: React.MouseEvent,
    consult: SessionListItem,
  ) => {
    event.stopPropagation();
    if (isInternallyScheduled(consult)) {
      window.open(consult.psychologistUrl, "_blank", "noopener");
    }
  };

  const handleClickRescheduleSession = (
    event: React.MouseEvent,
    consult: SessionListItem,
  ) => {
    event.stopPropagation();
    handleActionsClose(consult.id);
    if (isInternallyScheduled(consult)) {
      navigate(`/my-sessions/consult/${consult.id}/time-picker`);
    }
  };

  const handleClickCancelSession = (
    event: React.MouseEvent,
    consult: SessionListItem,
  ) => {
    event.stopPropagation();
    handleActionsClose(consult.id);
    if (isInternallyScheduled(consult)) {
      navigate(`/my-sessions/consult/${consult.id}/cancel-session`);
    }
  };

  const handleClickSessionRow = (
    event: React.MouseEvent,
    consult: SessionListItem,
  ) => {
    event.stopPropagation();
    if (showSessionDetails) {
      navigate(`/clients/${consult.user?.id}`);
    }
  };

  const handleClickCopyJoinLink = async (
    event: React.MouseEvent,
    consult: SessionListItem,
  ) => {
    event.stopPropagation();
    handleActionsClose(consult.id);
    await navigator.clipboard.writeText(consult.guestUrl!);
    snackbarShowMessage(t("SessionsView.LinkCopiedClipboard"), "success");
  };
  return (
    <>
      <PageTitle applicationName={t("Navigation.Title")} title={titleText} />
      {isSwapPsychologistOpen && (
        <SwapPsychologistDialog
          setIsOpen={setIsSwapPsychologistOpen}
          consult={selectedConsult}
        />
      )}
      {selectedConsult && (
        <ChangeSessionStatusDialog
          open={changeStatusOpen}
          setOpen={setChangeStatusOpen}
          consult={selectedConsult}
        />
      )}
      <Grid container spacing={4}>
        {isEmptyQueryResult(consults) ? (
          <NothingFound translationKey="Sessions.List.NotFound" />
        ) : (
          <Grid item xs={12}>
            <Paper className="px-3 py-4 rounded-md">
              <Box display="flex" alignItems="center">
                <Icon className="mr-2">fact_check_outlined</Icon>
                <h5 className="h5">{titleText}</h5>
                <TablePagination
                  component="div"
                  className="ml-auto -mr-2 mb-4"
                  count={total}
                  rowsPerPage={pageSize}
                  rowsPerPageOptions={[10]}
                  page={page}
                  onPageChange={(_, newPage) => {
                    onPageChange(newPage);
                  }}
                  slotProps={{
                    actions: {
                      nextButton: { disabled: isFetching || !hasNextPage },
                      previousButton: { disabled: isFetching || page === 0 },
                    },
                  }}
                />
              </Box>
              {!isFetching && (
                <TableContainer>
                  <Table
                    stickyHeader
                    aria-label="sticky table"
                    data-testid="all-sessions-table"
                    className="table-fixed"
                  >
                    <TableHead>
                      <TableRow>
                        {columns.map((column) => (
                          <TableCell
                            key={column.id}
                            style={{
                              minWidth: column.minWidth,
                              width: column.width,
                            }}
                            className="pl-1 py-4 sub1"
                          >
                            {t(column.id)}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {consults.map((consult) => (
                        <TableRow
                          hover
                          tabIndex={-1}
                          key={consult.id}
                          className="opacity-70"
                        >
                          <TableCell
                            className="py-2 px-1 sub1 hover:text-blue-600 cursor-pointer"
                            onClick={(event) => {
                              handleClickSessionRow(event, consult);
                            }}
                          >
                            <div className="flex flex-col">
                              <span>
                                {formatStandardDateOnly(
                                  consult.startedAt,
                                  AmsterdamTimezone,
                                )}
                              </span>
                              <span>
                                {formatTimeOnly(
                                  consult.startedAt,
                                  AmsterdamTimezone,
                                )}
                              </span>
                            </div>
                          </TableCell>
                          {showReferralToolkitIcon && (
                            <TableCell className="py-2 sub1 hover:text-blue-600">
                              {consult.employerOfClientHasReferralToolkit && (
                                <Tooltip
                                  title={t(
                                    "MySessions.EmployerReferralToolkitInfo.Tooltip",
                                  )}
                                  placement="top"
                                  data-testid="employer-referral-toolkit-info-column"
                                >
                                  <InfoOutlined data-testid="employer-referral-toolkit-info-icon" />
                                </Tooltip>
                              )}
                            </TableCell>
                          )}
                          <TableCell
                            className="py-2 px-1 sub1 cursor-pointer"
                            onClick={(event) => {
                              handleClickSessionRow(event, consult);
                            }}
                          >
                            {`${consult.user?.firstName} ${
                              consult.user?.lastName ?? ""
                            } `}
                          </TableCell>

                          <TableCell className="py-2 sub1 hover:text-blue-600">
                            {consult.consultType
                              ? formatFirstUpperCase(consult.consultType)
                              : ""}
                          </TableCell>
                          {showSessionLanguage && (
                            <TableCell className="justify-center">
                              <IconSessionLanguage
                                tooltip={t(
                                  `SessionLanguage.${consult.consultLanguage}`,
                                )}
                                language={consult.consultLanguage}
                                className="h-7"
                              />
                            </TableCell>
                          )}
                          {showSessionStage && (
                            <TableCell className="py-2 px-1 sub1 hover:text-blue-600">
                              {consult.consultStage}
                            </TableCell>
                          )}
                          {showPsychologistName && (
                            <TableCell
                              className="py-2 px-1 sub1 hover:text-blue-600"
                              data-cy="psychologist-column"
                            >
                              {`${consult.employee?.firstName} ${consult.employee?.lastName ?? ""}`}
                            </TableCell>
                          )}
                          <TableCell className="py-2 px-1 sub1">
                            <div className="flex">
                              <div className="flex flex-col align-center gap-1">
                                <span className="mr-2 hover:text-blue-600">
                                  {t(
                                    `Consults.Status.${formatFirstUpperCase(
                                      consult.status,
                                    )}`,
                                  )}
                                </span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="py-2 px-1 sub1">
                            <div className="flex items-center">
                              {consult.status!.toLowerCase() ===
                                STATUS.SCHEDULED.toLowerCase() && (
                                <>
                                  {showReassign && (
                                    <Tooltip
                                      title={t("ReassignPsychologist.Title")}
                                      placement="top"
                                      data-testid="reassign-session-button"
                                    >
                                      <SocialDistance
                                        className={buttonIconClassName}
                                        onClick={(event) => {
                                          handleClickSwapSession(
                                            event,
                                            consult,
                                          );
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                  {!showCancelSessionInMenu &&
                                    showCancelSession && (
                                      <Tooltip
                                        title={t("Common.CancelSession")}
                                        placement="top"
                                        data-testid="cancel-session-button"
                                      >
                                        <HighlightOffOutlined
                                          className={
                                            buttonIconClassName +
                                            (isInternallyScheduled(consult)
                                              ? ""
                                              : buttonIconDisabledClassName)
                                          }
                                          onClick={(event) => {
                                            handleClickCancelSession(
                                              event,
                                              consult,
                                            );
                                          }}
                                        />
                                      </Tooltip>
                                    )}

                                  {showJoinSession && (
                                    <Tooltip
                                      title={t("Common.JoinSession")}
                                      placement="top"
                                      data-testid="join-session-button"
                                    >
                                      <PlayCircleOutline
                                        className={
                                          buttonIconClassName +
                                          (isInternallyScheduled(consult)
                                            ? ""
                                            : buttonIconDisabledClassName)
                                        }
                                        onClick={(event) => {
                                          handleClickStartSession(
                                            event,
                                            consult,
                                          );
                                        }}
                                      />
                                    </Tooltip>
                                  )}

                                  {showActionsMenu && (
                                    <Button
                                      data-testid={`actions-${consult.id}`}
                                      onClick={(e) =>
                                        handleActionsClick(e, consult.id)
                                      }
                                      variant="tertiary"
                                      className="mr-2 rounded-sm [&&]:px-0 [&&]:py-0 h-8 w-8"
                                    >
                                      <MoreVertIcon className="text-2xl text-indigo-800" />
                                    </Button>
                                  )}

                                  {showActionsMenu && (
                                    <Menu
                                      key={consult.id}
                                      anchorEl={openTableMenus[consult.id]}
                                      open={Boolean(openTableMenus[consult.id])}
                                      onClose={() =>
                                        handleActionsClose(consult.id)
                                      }
                                      MenuListProps={{
                                        "aria-labelledby": "basic-button",
                                      }}
                                      slotProps={{
                                        paper: {
                                          className:
                                            "mt-1 rounded border border-solid border-slate-300",
                                        },
                                      }}
                                    >
                                      {showRescheduleSession && (
                                        <MenuItem
                                          onClick={(event) => {
                                            handleClickRescheduleSession(
                                              event,
                                              consult,
                                            );
                                          }}
                                          disabled={
                                            !isInternallyScheduled(consult)
                                          }
                                          data-testid={`reschedule-session-${consult.id}`}
                                          className="hover:text-blue-500 hover:bg-slate-100"
                                        >
                                          {t("Common.RescheduleSession")}
                                        </MenuItem>
                                      )}
                                      {showCancelSessionInMenu &&
                                        showCancelSession && (
                                          <MenuItem
                                            onClick={(event) => {
                                              handleClickCancelSession(
                                                event,
                                                consult,
                                              );
                                            }}
                                            disabled={
                                              !isInternallyScheduled(consult)
                                            }
                                            className="hover:text-blue-500 hover:bg-slate-100"
                                            data-testid={`cancel-session-${consult.id}`}
                                          >
                                            {t("Common.CancelSession")}
                                          </MenuItem>
                                        )}

                                      {showCopyJoinLink && (
                                        <MenuItem
                                          onClick={(event) =>
                                            handleClickCopyJoinLink(
                                              event,
                                              consult,
                                            )
                                          }
                                          className="hover:text-blue-500 hover:bg-slate-100"
                                          data-testid={`copy-link-${consult.id}`}
                                        >
                                          {t("SessionsView.CopyJoinLink")}
                                        </MenuItem>
                                      )}
                                    </Menu>
                                  )}
                                </>
                              )}
                              {showChangeSessionStatus &&
                                STATUS_SUBJECT_FOR_CHANGE.includes(
                                  consult.status!,
                                ) && (
                                  <Tooltip
                                    title={t("AllSessions.ChangeStatus")}
                                    placement="top"
                                  >
                                    <SyncAltOutlined
                                      data-testid="change-status-button"
                                      className={buttonIconClassName}
                                      onClick={(event) => {
                                        handleClickChangeSessionStatus(
                                          event,
                                          consult,
                                        );
                                      }}
                                    >
                                      sync_alt_outlined
                                    </SyncAltOutlined>
                                  </Tooltip>
                                )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {isFetching && (
                <div className="flex w-full justify-center">
                  <Spinner />
                </div>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default withSnackbar(SessionsView);
